// Category utility functions

export const getCategoryIcon = (category: string): string => {
    const categoryIcons: Record<string, string> = {
        'cleaning': '🧹',
        'delivery': '🚚',
        'tutoring': '📚',
        'photography': '📷',
        'writing': '✍️',
        'design': '🎨',
        'social-media': '📱',
        'moving': '📦',
        'event-assistance': '🎉',
        'tech-support': '💻',
        'handyman': '🔧',
        'pet-care': '🐕',
        'childcare': '👶',
        'cooking': '👨‍🍳',
        'gardening': '🌱',
        'other': '💼'
    }

    return categoryIcons[category] || '💼'
}

export const getCategoryColor = (category: string): string => {
    const categoryColors: Record<string, string> = {
        'cleaning': '#10B981',
        'delivery': '#3B82F6',
        'tutoring': '#8B5CF6',
        'photography': '#EF4444',
        'writing': '#F59E0B',
        'design': '#EC4899',
        'social-media': '#06B6D4',
        'moving': '#84CC16',
        'event-assistance': '#F97316',
        'tech-support': '#6366F1',
        'handyman': '#64748B',
        'pet-care': '#F59E0B',
        'childcare': '#EC4899',
        'cooking': '#EF4444',
        'gardening': '#10B981',
        'other': '#6B7280'
    }

    return categoryColors[category] || '#6B7280'
}

export const getCategoryName = (slug: string): string => {
    const categoryNames: Record<string, string> = {
        'cleaning': 'Cleaning',
        'delivery': 'Delivery',
        'tutoring': 'Tutoring',
        'photography': 'Photography',
        'writing': 'Writing',
        'design': 'Design',
        'social-media': 'Social Media',
        'moving': 'Moving',
        'event-assistance': 'Event Assistance',
        'tech-support': 'Tech Support',
        'handyman': 'Handyman',
        'pet-care': 'Pet Care',
        'childcare': 'Childcare',
        'cooking': 'Cooking',
        'gardening': 'Gardening',
        'other': 'Other'
    }

    return categoryNames[slug] || 'Other'
}

export const getCategoryDescription = (slug: string): string => {
    const categoryDescriptions: Record<string, string> = {
        'cleaning': 'House cleaning, office cleaning, and maintenance services',
        'delivery': 'Package delivery, food delivery, and courier services',
        'tutoring': 'Private tutoring and educational services',
        'photography': 'Event photography, portraits, and photo editing',
        'writing': 'Content writing, copywriting, and editing services',
        'design': 'Graphic design, web design, and creative services',
        'social-media': 'Social media management and digital marketing',
        'moving': 'Moving assistance and relocation services',
        'event-assistance': 'Event planning and assistance services',
        'tech-support': 'Technical support and IT services',
        'handyman': 'General repairs and maintenance services',
        'pet-care': 'Pet sitting, walking, and care services',
        'childcare': 'Babysitting and childcare services',
        'cooking': 'Meal preparation and catering services',
        'gardening': 'Landscaping and garden maintenance',
        'other': 'Miscellaneous services and gigs'
    }

    return categoryDescriptions[slug] || 'Miscellaneous services and gigs'
}

export const getFeaturedCategories = (): string[] => {
    return ['cleaning', 'delivery', 'tutoring', 'photography', 'writing', 'design']
}

export const getAllCategories = () => {
    const categories = [
        'cleaning', 'delivery', 'tutoring', 'photography', 'writing', 'design',
        'social-media', 'moving', 'event-assistance', 'tech-support', 'handyman',
        'pet-care', 'childcare', 'cooking', 'gardening', 'other'
    ]

    return categories.map(slug => ({
        slug,
        name: getCategoryName(slug),
        description: getCategoryDescription(slug),
        icon: getCategoryIcon(slug),
        color: getCategoryColor(slug),
        featured: getFeaturedCategories().includes(slug)
    }))
}

export const searchCategories = (query: string) => {
    const allCategories = getAllCategories()
    const lowerQuery = query.toLowerCase()

    return allCategories.filter(category =>
        category.name.toLowerCase().includes(lowerQuery) ||
        category.description.toLowerCase().includes(lowerQuery)
    )
}

export const getCategoryStats = (category: string) => {
    // This would typically come from an API
    // Using category parameter to avoid unused variable warning
    const categoryKey = category.toLowerCase()

    return {
        totalGigs: 0,
        averageRating: 0,
        averagePay: 0,
        popularTags: [],
        categoryKey
    }
}